import { throttle } from "lodash";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import React, { useEffect, useState } from "react";
import { useAppContext } from "../../../context/appContext";
import authApi from "../../../service/IdApi";

const PRESET_OPTIONS = [
    { label: "Active trial", value: "activeTrial" },
    { label: "Expire trial now", value: "expireTrial" },
    { label: "Paid", value: "paid" },
    { label: "Cancel at end", value: "cancelAtEnd" },
    { label: "Clear to free", value: "clearFree" },
];

const TIER_OPTIONS = [
    { label: "Starter", value: "starter" },
    { label: "Growth", value: "growth" },
    { label: "Business", value: "business" },
];

const tiersEnabledForPreset = (preset) =>
    preset === "activeTrial" || preset === "paid" || preset === "cancelAtEnd";

const defaultEndDate = () => {
    const date = new Date();
    date.setDate(date.getDate() + 7);
    return date;
};

const DevLicensePresetDialog = ({ visible, onHide, providerId, providerName }) => {
    const { displayAlert } = useAppContext();
    const [preset, setPreset] = useState("activeTrial");
    const [operateTier, setOperateTier] = useState("growth");
    const [promoteTier, setPromoteTier] = useState("growth");
    const [subscriptionEndAt, setSubscriptionEndAt] = useState(defaultEndDate());
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (visible) {
            setPreset("activeTrial");
            setOperateTier("growth");
            setPromoteTier("growth");
            setSubscriptionEndAt(defaultEndDate());
            setIsSaving(false);
        }
    }, [visible]);

    const tiersEnabled = tiersEnabledForPreset(preset);

    const applyPreset = throttle(async () => {
        if (!providerId || isSaving) {
            return;
        }
        setIsSaving(true);
        const body = {
            preset,
        };
        if (tiersEnabled) {
            body.operateTier = operateTier;
            body.promoteTier = promoteTier;
        }
        if (preset === "cancelAtEnd" && subscriptionEndAt) {
            body.subscriptionEndAt = subscriptionEndAt.toISOString();
        }
        try {
            await authApi.post("admin/provider/" + providerId + "/license-preset", body);
            displayAlert(
                "success",
                "License preset applied",
                "Re-login as this provider to refresh JWT-backed gates."
            );
            onHide();
        } catch (error) {
            const message = error?.response?.data?.message || "Please try again later";
            displayAlert("error", "License preset failed", message);
        } finally {
            setIsSaving(false);
        }
    }, 500, { trailing: false });

    return (
        <Dialog
            visible={visible}
            style={{ width: "520px" }}
            breakpoints={{ "640px": "95vw" }}
            header="Set license (dev)"
            modal
            closable={!isSaving}
            onHide={() => {
                if (!isSaving) {
                    onHide();
                }
            }}
            className="p-fluid"
        >
            <div className="flex flex-column gap-3">
                <div className="text-600 text-sm">
                    Development only. Applies a subscription preset for{" "}
                    <strong>{providerName || providerId}</strong>.
                </div>
                <div className="field mb-0">
                    <label htmlFor="licensePreset">Preset</label>
                    <Dropdown
                        id="licensePreset"
                        value={preset}
                        options={PRESET_OPTIONS}
                        onChange={(e) => setPreset(e.value)}
                        disabled={isSaving}
                    />
                </div>
                <div className="field mb-0">
                    <label htmlFor="operateTier">Operate tier</label>
                    <Dropdown
                        id="operateTier"
                        value={operateTier}
                        options={TIER_OPTIONS}
                        onChange={(e) => setOperateTier(e.value)}
                        disabled={isSaving || !tiersEnabled}
                    />
                </div>
                <div className="field mb-0">
                    <label htmlFor="promoteTier">Promote tier</label>
                    <Dropdown
                        id="promoteTier"
                        value={promoteTier}
                        options={TIER_OPTIONS}
                        onChange={(e) => setPromoteTier(e.value)}
                        disabled={isSaving || !tiersEnabled}
                    />
                </div>
                {preset === "cancelAtEnd" && (
                    <div className="field mb-0">
                        <label htmlFor="subscriptionEndAt">Subscription end date</label>
                        <Calendar
                            id="subscriptionEndAt"
                            value={subscriptionEndAt}
                            onChange={(e) => setSubscriptionEndAt(e.value)}
                            showIcon
                            disabled={isSaving}
                        />
                    </div>
                )}
                <div className="flex justify-content-end mt-2">
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        className="p-button-text mr-2"
                        onClick={onHide}
                        disabled={isSaving}
                    />
                    <Button
                        label="Apply"
                        icon="pi pi-check"
                        onClick={applyPreset}
                        loading={isSaving}
                    />
                </div>
            </div>
        </Dialog>
    );
};

export default DevLicensePresetDialog;
